import React, { useRef } from 'react';
import UpArrow from "../../assets/icons/UpArrowWhite.svg";
import Logo from "../../assets/images/MiddleburySeal220.gif";

/**
 * Input area component for submitting messages
 * @param {Object} props
 * @param {string} props.query - Current input text
 * @param {function} props.handleInputChange - Function to handle input changes
 * @param {function} props.handleKeyPress - Function to handle key presses
 * @param {function} props.handleSearch - Function to handle message submission
 * @param {number} props.charCount - Current character count
 * @param {number} props.MAX_INPUT_LENGTH - Maximum allowed input length
 * @param {number} props.arrowCycle - Animation cycle state
 * @param {boolean} props.isOnPhone - Whether the user is on a phone device
 * @returns {JSX.Element} The input area component
 */
const InputArea = ({
  query,
  handleInputChange,
  handleKeyPress,
  handleSearch,
  charCount,
  MAX_INPUT_LENGTH,
  arrowCycle,
  isOnPhone
}) => {
  const inputRef = useRef(null);
  const inputContainerRef = useRef(null);

  return (
    <main
      className={`
        ${isOnPhone
          ? 'fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-transparent'
          : 'relative h-fit px-4 pb-4 md:px-8'
        }
      `}
      style={{
        transition: 'transform 0.3s ease-out',
        backdropFilter: isOnPhone ? '' : 'none',
      }}
    >
      {/* Gradient divider - hidden on mobile */}
      <div
        className={`
          bg-gradient-to-b absolute -mt-1 from-transparent to-[#f2f2f2] 
          dark:to-[#1e1e1e] h-1 w-full ${isOnPhone ? 'hidden' : ''}
        `}
      ></div>

        <div
            ref={inputContainerRef}
            className={`
          ${isOnPhone ? "mx-0" : "max-w-3xl mx-auto"} 
          w-full transition-all duration-300 ease-in-out 
          ${isOnPhone ? 'pb-2' : ''}
        `}
        >
            <div
                className={`
            bg-white dark:bg-black rounded-xl shadow-md px-4 py-4 
            border dark:border-[#2f2f2f] dark:hover:border-[#3f3f3f] 
            transition ${isOnPhone ? 'shadow-lg' : ''}
          `}
            >
                <div className="relative flex items-center">
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask me about campus resources..."
                        maxLength={MAX_INPUT_LENGTH}
                        className="w-full py-2 xs:px-4 outline-none dark:text-white dark:bg-black rounded-lg"
                    />

                    {/* Character counter */}
                    <div
                        className={`
                ${isOnPhone && "hidden"} w-fit text-xs 
                ${charCount > MAX_INPUT_LENGTH * 0.8 ? 'text-red-500' : 'text-gray-400 dark:text-gray-500'}
              `}
                    >
                        {charCount}/{MAX_INPUT_LENGTH}
                    </div>

                    <button
                        onClick={handleSearch}
                        disabled={query.length === 0}
                        className={`
                h-10 aspect-square group flex-shrink-0 
                ${query.length !== 0
                            ? "hover:bg-blue-600 bg-blue-700 opacity-100"
                            : "bg-blue-600 opacity-50"
                        } 
                transition p-1 rounded-lg flex items-center justify-center ml-2 
                overflow-hidden relative
              `}
                    >
                        <div className="relative w-full h-full">
                            {/* First arrow - starts visible, moves up and out */}
                            <img
                                src={UpArrow}
                                alt="Send"
                                className={`
                    absolute w-full h-full object-contain transition-transform duration-500 
                    ${arrowCycle % 2 === 1 ? '-translate-y-full' : ''} 
                    ${query.length !== 0 && "hover:-translate-y-0.5"}
                  `}
                                key={`arrow-1-${Math.floor(arrowCycle / 2)}`}
                            />
                            {/* Second arrow - starts below, moves up to center */}
                            <img
                                src={UpArrow}
                                alt="Send"
                                className={`
                    absolute w-full h-full object-contain transition-transform duration-500 
                    ${arrowCycle % 2 === 1 ? 'translate-y-0' : 'translate-y-full'}
                  `}
                                key={`arrow-2-${Math.floor(arrowCycle / 2)}`}
                            />
                        </div>
                    </button>
                </div>
            </div>
            <div className={`w-full h-fit flex justify-center mt-3 ${isOnPhone ? 'hidden' : ''}`}>
                <div className="bg-white text-[#00000075] hover:text-black hover:border-[#00000050] dark:bg-[#1e1e1e] space-x-2 dark:text-[#ffffff85] dark:hover:text-white px-2 py-1 text-xs dark:hover:bg-[#2e2e2e] border dark:border-[#2f2f2f] dark:hover:border-[#3f3f3f] transition cursor-pointer w-fit h-fit rounded-lg flex items-center">
                    <img
                        src={Logo}
                        className="w-5 h-5 transition-transform duration-300"
                        alt="Middlebury Logo"
                    />
                    <div className="font-Poppins">Middlebury College</div>
                </div>
            </div>
        </div>
    </main>
  );
};

export default InputArea;